import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Building2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BG = "https://images.pexels.com/photos/18153132/pexels-photo-18153132.jpeg";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      login(data);
      toast.success(`Welcome back, ${data.user.name.split(" ")[0]}`);
      nav("/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/70" />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-lg bg-white/15 backdrop-blur flex items-center justify-center">
              <Building2 className="h-5 w-5" aria-hidden="true" />
            </div>
            <span className="font-heading text-xl font-bold">Nustamac / Walo</span>
          </div>
          <div>
            <p className="overline text-white/70 mb-3">Property Care · Kumba</p>
            <h1 className="font-heading text-4xl xl:text-5xl font-bold tracking-tight leading-tight max-w-md">
              Your property, cared for — even from across the world.
            </h1>
            <p className="mt-4 text-white/80 max-w-md">
              Inspect, build, transform and maintain your property with full transparency.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-sm fade-up">
          <div className="lg:hidden flex items-center gap-2 mb-8">
            <div className="h-9 w-9 rounded-lg bg-primary flex items-center justify-center">
              <Building2 className="h-5 w-5 text-primary-foreground" aria-hidden="true" />
            </div>
            <span className="font-heading text-lg font-bold">Nustamac / Walo</span>
          </div>
          <p className="overline text-accent mb-2">Welcome back</p>
          <h2 className="font-heading text-3xl font-bold tracking-tight mb-6">Sign in</h2>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input" placeholder="you@example.com" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input" placeholder="••••••••" />
            </div>
            {error && <p className="text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full rounded-full hover:bg-primary/90">
              {loading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Sign in
            </Button>
          </form>
          <p className="text-sm text-muted-foreground mt-6">
            No account?{" "}
            <Link to="/register" className="text-primary font-medium hover:underline" data-testid="go-register-link">Create one</Link>
          </p>
          <div className="mt-6 rounded-lg border border-border bg-secondary/50 p-3 text-xs text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Demo diaspora customer</p>
            diaspora@example.com · demo1234
          </div>
        </div>
      </div>
    </div>
  );
}
